import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatTimeToHHMM } from '../../../../utils/localeUtils';

const PanelTitle = styled.h2`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin-bottom: 10px;
  margin-top: 10px;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const PanelTitleH3 = styled.h3`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin-bottom: 0px;
  margin-top: 10px;

  @media (max-width: 768px) {
    margin-bottom: 0;
    margin-top: 0;

    &:first-child,
    &:first-of-type {
      margin-top: 10px;
    }
  }
`;

const SectionTitle = styled.h2`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin: 5px 0;
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: #2D3653;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const TimeTableCol3 = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$displayRoom ? '4fr 2fr 2fr' : '4fr 1fr'};
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.2);
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const CourseTable = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.2);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TimeLabel = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans JP', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${props => props.$fontSize ? `clamp(${props.$fontSize}px, 1vw, ${props.$fontSize}px)` : 'clamp(14px, 1vw, 18px)'};
`;

const TimeValue = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${props => props.$fontSize ? `clamp(${props.$fontSize}px, 1vw, ${props.$fontSize}px)` : 'clamp(14px, 1vw, 18px)'};
`;

const DoubleSeparator = styled.div`
  height: 6px;
  margin: 20px auto;
  width: 70%;
  border-top: 2px solid #2D3653;
  border-bottom: 2px solid #2D3653;
`;

const ContentWrapper = styled.div`
  overflow: hidden;
  // height: calc(100% - 60px);
`;

const ScheduleContent = ({ showShabbatInfo, parasha, sortedSections, shiur, formatTime, formatShiurTime, t, displayRoom, fontSize, community, language }) => (
  <>
    {showShabbatInfo && parasha && (
      <>
        <PanelTitleH3>{t('display.shabbat')} {parasha.parasha}</PanelTitleH3>
        <PanelTitleH3>
          {t('display.candleLighting')} {community?.candleLightHour ? formatTimeToHHMM(community.candleLightHour, language) : formatTime(parasha.candleLighting)} - {t('display.havdalah')} {formatTime(parasha.havdalah)}
        </PanelTitleH3>
      </>
    )}
    
    {sortedSections.map(({ sectionName, items }) => (
      <React.Fragment key={`section-${sectionName}`}>
        <SectionTitle>{sectionName}</SectionTitle>
        {items.map((item, index) => (
          <TimeTableCol3 key={`shabat-${sectionName}-${index}`} $displayRoom={displayRoom}>
            <TimeLabel $fontSize={fontSize}>{item.name}</TimeLabel>
            {displayRoom && <TimeLabel $fontSize={fontSize}>{item.roomName}</TimeLabel>}
            <TimeValue $fontSize={fontSize}>{item.time}</TimeValue>
          </TimeTableCol3>
        ))}
      </React.Fragment>
    ))}

    {shiur?.saturday && shiur.saturday.length > 0 && (
      <>
        <DoubleSeparator />
        <PanelTitle>{t('display.courseShabbat')}</PanelTitle>
        {shiur.saturday
          .sort((a, b) => a.time.localeCompare(b.time))
          .map((item, index) => (
            <CourseTable key={`shiur-shabbat-${index}`}>
              <TimeLabel $fontSize={fontSize}>{item.subject}{displayRoom && item.roomName ? ` - ${item.roomName}` : ''}</TimeLabel>
              <TimeValue $fontSize={fontSize}>
                {formatShiurTime(item.time, item.endTime)}
              </TimeValue>
            </CourseTable>
          ))}
      </>
    )}
  </>
);

const ShabbatAndCourseScheduleContainer = ({
  isTransitioning,
  parasha,
  shabatSchedule,
  shiur,
  formatTime,
  formatShiurTime,
  AnimatedContent,
  showShabbatInfo = true,
  displayRoom=true,
  fontSize,
  community,
  language
}) => {
  const { t } = useTranslation();

  // Grouper les horaires par section et trier
  const groupedSchedule = shabatSchedule.reduce((acc, item) => {
    const section = item.sectionName || 'default';
    if (!acc[section]) {
      acc[section] = {
        name: section,
        rank: item.rank !== undefined ? item.rank : 999,
        items: []
      };
    }
    acc[section].items.push(item);
    return acc;
  }, {});

  // Trier les sections par rank
  const sortedSections = Object.values(groupedSchedule)
    .sort((a, b) => {
      return (a.rank !== undefined ? a.rank : 999) - (b.rank !== undefined ? b.rank : 999);
    })
    .map(section => ({
      sectionName: section.name,
      items: section.items.sort((a, b) => {
        if (a.rank !== b.rank) {
          return (a.rank !== undefined ? a.rank : 999) - (b.rank !== undefined ? b.rank : 999);
        }
        return a.time.localeCompare(b.time);
      })
    }));

  const contentProps = {
    showShabbatInfo,
    parasha,
    sortedSections,
    shiur,
    formatTime,
    formatShiurTime,
    t,
    displayRoom,
    fontSize,
    community,
    language
  };

  return (
    <ContentWrapper>
      {isTransitioning === undefined ? (
        <div>
          <ScheduleContent {...contentProps} />
        </div>
      ) : (
        <AnimatedContent $isEntering={!isTransitioning}>
          <ScheduleContent {...contentProps} />
        </AnimatedContent>
      )}
    </ContentWrapper>
  );
};

export default ShabbatAndCourseScheduleContainer; 